/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import { Formik } from 'formik';
import { FiUpload, FiX } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { FormInput, FormSelect, FormTextarea } from '../../../Shared/Forms/index';
import ProductSchema from '../../../../schemas/productSchema';

function ProductForm({
  initialValues,
  onSubmit,
  isLoading,
  formTitle,
  submitButtonText,
  categories,
  units,
}) {
  return (
    <div className="p-6 max-w-4xl mx-auto bg-white rounded-lg shadow">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">{formTitle}</h1>
      <Formik
        initialValues={initialValues}
        validationSchema={ProductSchema}
        onSubmit={onSubmit}
      >
        {({
          values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue,
        }) => (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="w-full sm:w-1/3">
                <label htmlFor="product-image-upload" className="block text-sm font-medium text-gray-700 mb-2">
                  Product Image
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  {values.image && typeof values.image !== 'string' ? (
                    <div className="relative">
                      <img
                        src={URL.createObjectURL(values.image)}
                        alt="Preview"
                        className="h-40 mx-auto rounded-md object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => setFieldValue('image', null)}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                        aria-label="Remove image"
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <FiUpload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600 items-center justify-center">
                        <label
                          htmlFor="product-image-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                        >
                          <span>Upload an image</span>
                          <input
                            id="product-image-upload"
                            name="image"
                            type="file"
                            className="sr-only"
                            onChange={(e) => setFieldValue('image', e.currentTarget.files[0])}
                            accept="image/*"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG up to 2MB</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full sm:w-2/3 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="Product Name (English)"
                    id="productName"
                    name="productName"
                    value={values.productName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.productName && errors.productName}
                    required
                  />
                  <FormInput
                    label="Product Name (বাংলা)"
                    id="productNameBn"
                    name="productNameBn"
                    value={values.productNameBn}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.productNameBn && errors.productNameBn}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSelect
                    label="Category"
                    id="category"
                    name="category"
                    value={values.category}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.category && errors.category}
                    options={categories}
                    required
                  />
                  <FormInput
                    label="Subcategory"
                    id="subCategory"
                    name="subCategory"
                    value={values.subCategory}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.subCategory && errors.subCategory}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="Barcode/SKU"
                    id="barcode"
                    name="barcode"
                    value={values.barcode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.barcode && errors.barcode}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FormInput
                      label="Purchase Price (৳)"
                      id="purchasePrice"
                      name="purchasePrice"
                      type="number"
                      value={values.purchasePrice}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.purchasePrice && errors.purchasePrice}
                    />
                    <FormInput
                      label="Selling Price (৳)"
                      id="sellingPrice"
                      name="sellingPrice"
                      type="number"
                      value={values.sellingPrice}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.sellingPrice && errors.sellingPrice}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormInput
                      label="Initial Quantity"
                      id="quantity"
                      name="quantity"
                      type="number"
                      value={values.quantity}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.quantity && errors.quantity}
                    />
                    <FormSelect
                      label="Unit"
                      id="unit"
                      name="unit"
                      value={values.unit}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.unit && errors.unit}
                      options={units}
                    />
                  </div>
                  <FormInput
                    label="Minimum Stock Level"
                    id="minStockLevel"
                    name="minStockLevel"
                    type="number"
                    value={values.minStockLevel}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.minStockLevel && errors.minStockLevel}
                  />
                </div>

                <FormTextarea
                  label="Description"
                  id="description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.description && errors.description}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setFieldValue('image', null);
                  handleSubmit();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : submitButtonText}
              </button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}

ProductForm.propTypes = {
  initialValues: PropTypes.shape({
    productName: PropTypes.string,
    productNameBn: PropTypes.string,
    category: PropTypes.string,
    subCategory: PropTypes.string,
    barcode: PropTypes.string,
    purchasePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sellingPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unit: PropTypes.string,
    minStockLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    image: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(File),
      PropTypes.oneOf([null]),
    ]),
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  formTitle: PropTypes.string,
  submitButtonText: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  units: PropTypes.arrayOf(PropTypes.string).isRequired,
};

ProductForm.defaultProps = {
  isLoading: false,
  formTitle: 'Product Form',
  submitButtonText: 'Submit',
};

export default ProductForm;
